import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, catchError, finalize, forkJoin, map, of } from 'rxjs';
import { AuthService } from 'src/app/module/auth/service/auth.service';
import { FrontendConfigApi } from 'src/app/shared/api/frontend-config.api';
import { googleMapLoader } from 'src/app/shared/initializer/google-map';
import { AppState } from 'src/app/shared/state/app.state';
import { CompanyState } from 'src/app/shared/state/company.state';
import { UserState } from 'src/app/shared/state/user.state';
import { environment as ENV, environment } from 'src/environments/environment';
import { API_ACTION, API_SCOPE, ApiAction, ApiScope } from '../interceptor/interceptor.token';
import {
  AllApplicableSettings,
  AllCompanyDetailsResponse,
  AppEnumMap,
  AppPermission,
  ApplicationSettingApiResponse,
  CommonApiResponse,
  EnumItem,
  UserPermittedActionsResponse,
} from '../interface/common.interface';
import { CompanySetting } from '../interface/company.interface';
import { UserSetting, UserSettingApiResponse } from '../interface/user.interface';
import { ProgressLoaderService } from '../services/progress-loader.service';

const API_BASE_URL = environment.backendBaseUrl;

@Injectable({
  providedIn: 'root',
})
export class InitializerService {
  constructor(
    private _cookieService: CookieService,
    private frontendConfigApi: FrontendConfigApi,
    private _http: HttpClient,
    private appState: AppState,
    private authService: AuthService,
    private companyState: CompanyState,
    private userState: UserState,
    private progressLoaderService: ProgressLoaderService
  ) {
    googleMapLoader(environment.googleMapsApiKey);
  }

  initializeApp(): Observable<void> {
    this._cookieService.delete('profet-ctx', '/', ENV.appDomain, true, 'None');
    const loaderId = this.progressLoaderService.show('Loading Profet Valuation');
    return forkJoin({
      enums: this._getAppEnumeration(),
      setting: this._getAppSetting(),
      userPermittedActions: this._getAccessSetting(),
      fieldConfigs: this.frontendConfigApi.getFieldConfigs(),
    }).pipe(
      map(({ enums, setting, userPermittedActions, fieldConfigs }) => {
        this.appState.applicationSettings = setting.applicationSettings;

        this.userState.userSetting = this._formatUserSetting(setting.companySettings, setting.userSettings);

        this.userState.permittedActions = userPermittedActions;

        this.companyState.setPropertyFieldConfig('DEFAULT', fieldConfigs.propertyField);
        this.companyState.setOrderFieldConfig('DEFAULT', fieldConfigs.orderField);
        this.companyState.setEnumConfig('DEFAULT', this._formatAppEnumeration(enums));

        this._formatAndLoadCompanySettings(setting.companySettings);

        if (this.userState.permittedActions.includes(AppPermission.COMPANY_SWITCH)) {
          this._getAllCompanies();
        }
      }),
      catchError((error: unknown) => {
        this.authService.logout();
        console.error('Error initializing app', error);
        return of();
      }),
      finalize(() => setTimeout(() => this.progressLoaderService.hide(loaderId), 1000))
    );
  }

  private _getAppEnumeration(): Observable<AppEnumMap> {
    return this._http
      .get<
        CommonApiResponse<AppEnumMap>
      >(`${API_BASE_URL}/api/getAllEnumerations`, { context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.COMPANY) })
      .pipe(map((res) => res.data));
  }

  private _getAppSetting(): Observable<AllApplicableSettings> {
    return this._http
      .get<
        CommonApiResponse<ApplicationSettingApiResponse>
      >(`${API_BASE_URL}/api/getApplicableSettings`, { context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.COMPANY) })
      .pipe(map((res) => res.data.applicableSettings));
  }

  private _getAccessSetting(): Observable<AppPermission[]> {
    return this._http
      .get<
        CommonApiResponse<UserPermittedActionsResponse>
      >(`${API_BASE_URL}/api/accessControl`, { context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.COMPANY) })
      .pipe(map((res) => res.data.permittedActions as AppPermission[]));
  }

  private _getAllCompanies() {
    this._http
      .get<CommonApiResponse<AllCompanyDetailsResponse>>(
        `${API_BASE_URL}/api/companySettingses/search/findAllCompanies`,
        { context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.COMPANY) }
      )
      .pipe(
        map(
          (res) =>
            res.data.companies?.map((company) => {
              return {
                key: company.companyKey,
                displayValue: company.companyName,
              };
            }) ?? []
        ),
        catchError(() => {
          return of([]);
        })
      )
      .subscribe((res) => (this.companyState.companyList = res));
  }

  private _formatAppEnumeration(appEnums: AppEnumMap): AppEnumMap {
    const formattedEnum: any = {};
    Object.entries(appEnums).forEach(([key, valueList]: [string, EnumItem[]]) => {
      formattedEnum[key] = valueList.map((value: EnumItem) => {
        value.key = value.enumValue as string;
        value.displayValue = value.enumDisplayValue as string;
        return value;
      });
    });
    return formattedEnum as AppEnumMap;
  }

  private _formatUserSetting(companySetting: CompanySetting, userSetting: UserSettingApiResponse): UserSetting {
    const companyKey = companySetting.companyKey;

    return {
      ...userSetting,
      roles:
        userSetting.roles
          ?.filter((roleConfig) => roleConfig.companyKey == companyKey)
          .map((roleConfig) => roleConfig.role) ?? [],
    };
  }

  private _formatAndLoadCompanySettings(companySetting: CompanySetting): void {
    const enabledReports: string[] = [];

    this.companyState.getEnumConfig().reportType.forEach((reportType) => {
      if (!companySetting[reportType.key]?.enabled) {
        delete companySetting[reportType.key];
      } else {
        enabledReports.push(reportType.key);
      }
    });

    this.companyState.companySetting = companySetting;
    this.companyState.enabledReportList = enabledReports;
  }
}
